import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { EvaAvatar, EvaFeedback } from './Eva';

const BLUE = '#3a6ad9';
const GREEN = '#acf44e';
const DARK = '#111827';
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// sessionStorage, NOT localStorage: the transcript survives a reload — the
// actual requirement — without remaining recoverable after the browser closes.
// This is mortgage data on a possibly shared device, so persisting it further
// than the tab's lifetime buys nothing and costs privacy.
// NOT @capacitor/preferences: that dependency was deliberately reverted.
// Keyed per userType so a borrower and a partner on one browser can't restore
// each other's thread. Server-side the session is ownership-gated anyway — a
// sessionId the caller doesn't own returns an empty transcript, never another
// user's — so this key is tidiness, not the security boundary.
const sessionKeyFor = (userType) => `doorly_eva_session_${userType}`;

// One-time launcher prompt. localStorage, NOT sessionStorage: "once ever" rather
// than "once per tab". A dismissal flag is not mortgage data, so it does not
// carry the privacy reason the transcript key does.
const PROMPT_SEEN_KEY = 'doorly_eva_prompt_seen';

// Any surface can open the widget by dispatching this. A custom event rather
// than lifted state or a context: the widget is mounted once in AppShell and the
// openers are scattered (nav, cards, empty states), so a global signal keeps
// them from all needing a shared parent.
export const OPEN_EVA_EVENT = 'doorly:open-eva';
const PROMPT_AUTO_DISMISS_MS = 5000;

// ── Where Eva does not appear ────────────────────────────────────────────────
// ⚠️ THE REASON IS NOT GEOMETRY. On /messages the bubble happened to land on
// the composer's Send button, which is how this was noticed — but moving the
// bubble would NOT be a fix. /messages IS the borrower talking to Doorly. A
// second, different chat interface floating on top of the real conversation is
// wrong wherever it sits, and the borrower cannot be expected to know which of
// two chat windows reaches a person. Do not "fix" this by repositioning.
//
// ⚠️ THE OTHER ENTRIES ARE NOT COLLISIONS AT ALL — they are surfaces where a
// conversational assistant should not be offering help beside a legal act.
// Eva is an AI. On a page where the borrower is SIGNING something, or acting
// on wire instructions, an adjacent chat window that answers confidently is a
// place for a wrong answer to attach itself to a decision that cannot be taken
// back. Wire fraud in particular works by inserting a plausible helper next to
// payment instructions; Doorly must not ship one.
//
// Support is not removed on these routes — the borrower still has /messages,
// which reaches a person, and every one of these pages carries its own contact
// path. What is removed is the AI offering to answer beside the act.
const EVA_SUPPRESSED_ROUTES = [
  '/messages',                    // the real conversation — see above
  '/sign-ab-psa',                 // signing
  '/disclosure',                  // signing (token route)
  '/offer-review',                // signing (token route)
  '/portal/closing-disclosures',  // signing + figures
  '/portal/loan-docs',            // signing
  '/portal/final-review',         // wire instructions and final figures
  '/gift-letter',                 // signed attestation
  '/vor', '/vom', '/rent-free',   // signed third-party attestations
];

function isSuppressedPath(pathname = '') {
  return EVA_SUPPRESSED_ROUTES.some(
    (r) => pathname === r || pathname.startsWith(`${r}/`)
  );
}

export { EVA_SUPPRESSED_ROUTES, isSuppressedPath };

// 🔑 dockedBelowLg (dashboard stages spec B8, 2026-11-03): on a page that docks Eva in the phone top bar (AppShell — the
// Dashboard), the floating launcher is not drawn below 1024px while the panel is closed, so it never covers card content;
// the top-bar button opens her. While the panel is open the launcher shows as usual (its ✕ closes it).
export default function DoorlyChat({ token, userType = 'borrower', dockedBelowLg = false }) {
  // ⚠️ Called unconditionally, before any early return — a hook after a
  // conditional return is the classic order violation.
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 1023px)').matches : false));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 1023px)');
    const on = () => setNarrow(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  // Restore has to settle BEFORE the greeting effect runs, or the greeting would
  // be prepended to a restored transcript.
  const [restored, setRestored] = useState(false);
  // Launcher prompt. On desktop this follows hover; on touch there IS no hover,
  // so it shows once on first visit and auto-dismisses — otherwise it would be
  // permanently invisible to most users, this being a Capacitor native build.
  // Same lesson as the always-visible feedback thumbs.
  const [showPrompt, setShowPrompt] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [resolutionAsk, setResolutionAsk] = useState(null);
  const [resolutionDone, setResolutionDone] = useState(null);
  const [resolutionQuestion, setResolutionQuestion] = useState(null);
  const [csatScore, setCsatScore] = useState(null);
  const [resolutionAskMessageId, setResolutionAskMessageId] = useState(null);
  const [contactPref, setContactPref] = useState('phone');
  const [contactVal, setContactVal] = useState('');
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);
  useEffect(() => { if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 100); } }, [open]);

  // Touch-only first-visit prompt. `(hover: none)` is the capability, not a
  // user-agent guess — a touchscreen laptop with a mouse still gets the hover
  // behaviour, which is the correct outcome.
  useEffect(() => {
    let seen = true;
    try { seen = !!localStorage.getItem(PROMPT_SEEN_KEY); } catch (_) { /* private mode → treat as seen, never nag */ }
    if (seen) return;
    const noHover = typeof window !== 'undefined'
      && window.matchMedia && window.matchMedia('(hover: none)').matches;
    if (!noHover) return;   // desktop discovers it by hovering
    setShowPrompt(true);
    try { localStorage.setItem(PROMPT_SEEN_KEY, '1'); } catch (_) {}
    const t = setTimeout(() => setShowPrompt(false), PROMPT_AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, []);

  // Never leave it up once the widget is open — it would sit behind the panel.
  useEffect(() => { if (open) setShowPrompt(false); }, [open]);

  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener(OPEN_EVA_EVENT, openIt);
    return () => window.removeEventListener(OPEN_EVA_EVENT, openIt);
  }, []);

  // Persist the session id so a reload can pick the thread back up. Ratings come
  // back with the transcript (GET /history returns them), so a thumbed answer
  // still shows as thumbed after a refresh.
  useEffect(() => {
    if (sessionId) {
      try { sessionStorage.setItem(sessionKeyFor(userType), sessionId); } catch (_) { /* private mode */ }
    }
  }, [sessionId, userType]);

  useEffect(() => {
    if (!open || restored) return;
    let cancelled = false;
    (async () => {
      let stored = null;
      try { stored = sessionStorage.getItem(sessionKeyFor(userType)); } catch (_) { /* private mode */ }
      if (!stored) { if (!cancelled) setRestored(true); return; }
      try {
        const res = await fetch(`${API}/api/chat/history/${stored}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!cancelled && res.ok && Array.isArray(data.messages) && data.messages.length) {
          setSessionId(stored);
          setMessages(data.messages.map((m) => ({
            role: m.role === 'assistant' ? 'assistant' : 'user',
            content: m.content,
            id: m.id ?? null,
            rating: m.rating ?? null,
          })));
        } else if (!cancelled) {
          // Empty means the session is gone or was never ours — drop the key
          // rather than retrying it on every open.
          try { sessionStorage.removeItem(sessionKeyFor(userType)); } catch (_) {}
        }
      } catch (_) { /* offline → fall through to the greeting */ }
      if (!cancelled) setRestored(true);
    })();
    return () => { cancelled = true; };
  }, [open, restored, token, userType]);

  useEffect(() => {
    if (open && restored && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        // Partner copy states the capability PRECISELY rather than generously.
        // The previous line ("ask me anything about ... your buyers") invited the
        // exact questions Eva must refuse — a buyer's income, credit, or why they
        // were declined. "Where your buyers stand and what they need to do next"
        // is the whole of what she may disclose: the portal's own status label
        // and next step, and nothing beyond it.
        content: userType === 'borrower'
          ? "Hi! I'm Eva, your Doorly assistant. Ask me anything — or I can help you figure out your next step."
          : "Hi! I'm Eva, your DoorlyPro assistant. I can tell you where your buyers stand and what they need to do next, help you submit a buyer, or answer questions about DoorlyPro and the program.",
      }]);
    }
  }, [open, restored]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async () => {
    const text = input.trim();
    // Escalation is not terminal — the borrower can keep chatting after a ticket.
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: sessionId, user_type: userType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Server error');
      if (data.session_id) setSessionId(data.session_id);
      if (data.escalated) setEscalated(true);
      // id is what makes this answer rateable; null on older backends, in which
      // case the thumbs simply don't render for that message.
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply, id: data.message_id ?? null, rating: null }]);
      // ⚠️ EVA ASKS, ONCE. The per-answer thumbs beside every reply have been
      // clicked zero times across every session ever — a control waiting to be
      // noticed is not a measurement. The server claims the single ask per
      // session, so this only ever arrives once.
      if (data.resolution_ask) setResolutionAsk({ ...data.resolution_ask, messageId: data.message_id ?? null });
      if (data.escalate) setShowEscalate(true);
      if (!open) setUnread(u => u + 1);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `I'm having trouble right now: ${err.message}. Please try again in a moment.` }]);
    } finally { setLoading(false); }
  };

  // ⚠️ THE ANSWER IS OPTIMISTIC AND UNFAILING. Someone answered a question we
  // asked; if our write fails that is ours to lose, not something to render back
  // at them as an error.
  const answerResolution = async (rating) => {
    const ask = resolutionAsk;
    setResolutionDone(rating);
    setResolutionQuestion(ask?.score_question || 'How did Eva do?');
    setResolutionAskMessageId(ask?.messageId ?? null);
    setResolutionAsk(null);
    try {
      await fetch(`${API}/api/chat/resolution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: sessionId, message_id: ask?.messageId ?? null, rating }),
      });
    } catch (_) { /* the data point is ours to lose, not theirs */ }
  };

  const rateEva = async (score) => {
    setCsatScore(score);
    try {
      await fetch(`${API}/api/chat/resolution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: sessionId, message_id: resolutionAskMessageId, score }),
      });
    } catch (_) { /* the data point is ours to lose, not theirs */ }
  };

  // Per-answer helpfulness rating. Optimistic: the thumb fills immediately and
  // reverts only if the write fails — a feedback control that feels laggy simply
  // doesn't get used. Clicking the active thumb again clears the rating.
  const rateMessage = async (msgId, next) => {
    if (!msgId) return;
    const prevRating = messages.find(m => m.id === msgId)?.rating ?? null;
    const value = prevRating === next ? 0 : next;   // same thumb twice → clear
    setMessages(prev => prev.map(m => (m.id === msgId ? { ...m, rating: value || null } : m)));
    try {
      const res = await fetch(`${API}/api/chat/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message_id: msgId, rating: value }),
      });
      if (!res.ok) throw new Error('save failed');
    } catch {
      setMessages(prev => prev.map(m => (m.id === msgId ? { ...m, rating: prevRating } : m)));
    }
  };

  const handleEscalate = async () => {
    if (!contactVal.trim()) return;
    try {
      const res = await fetch(`${API}/api/chat/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, contact_preference: contactPref, contact_value: contactVal.trim(), user_type: userType }),
      });
      const data = await res.json().catch(() => ({}));
      setShowEscalate(false);
      if (data && data.success) {
        setEscalated(true);
        const ref = data.ticket_number ? ` Your reference number is ${data.ticket_number}.` : '';
        setMessages(prev => [...prev, { role: 'assistant', content: `A Doorly team member will reach out within 1 business day.${ref} You can keep asking me anything else in the meantime.` }]);
      } else {
        // Honest failure — never claim someone will follow up if no ticket opened.
        setMessages(prev => [...prev, { role: 'assistant', content: "I'm sorry — I couldn't open a support ticket just now. Please reach the Doorly team directly from the Messages page and they'll follow up." }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm sorry — I couldn't open a support ticket just now. Please reach the Doorly team directly from the Messages page and they'll follow up." }]);
    }
  };

  // ⚠️ SUPPRESSED AFTER EVERY HOOK HAS RUN, NOT BEFORE. Returning early from
  // the top of the component would change the number of hooks called between
  // renders the moment a borrower navigates onto or off one of these routes.
  // The widget's own state (transcript, restore, greeting) is preserved, so
  // leaving the route brings Eva back exactly as she was.
  if (isSuppressedPath(pathname)) return null;

  return (
    <>
      {/* Launcher prompt. Sits to the LEFT of the 56px launcher, vertically
          centred on it. pointerEvents none so it can never intercept the tap
          that opens the widget — the bubble is an invitation, not a target. */}
      {showPrompt && !open && (
        <div
          role="status"
          style={{
            position: 'fixed', bottom: 'calc(26px + env(safe-area-inset-bottom, 0px))', right: 88, zIndex: 9998,
            background: '#fff', color: DARK, padding: '9px 13px',
            borderRadius: '14px 14px 4px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.16)',
            fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            pointerEvents: 'none',
            animation: 'doorlyPromptIn 0.18s ease-out',
          }}
        >
          Hi, I&rsquo;m Eva. Ask me anything.
        </div>
      )}

      {!(dockedBelowLg && narrow && !open) && <button data-testid="eva-launcher" onClick={() => setOpen(o => !o)} style={{ position: 'fixed', bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))', right: 20, width: 56, height: 56, borderRadius: '50%', background: BLUE, border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, transition: 'transform 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; if (!open) setShowPrompt(true); }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; setShowPrompt(false); }}>
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fffd5b" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          // ⚠️ A MESSAGE ICON, NOT EVA'S FACE — matching the marketing widget.
          // This deliberately replaces a considered decision: the previous
          // <EvaAvatar size={52} /> was tuned so the 2px blue rim read as an
          // accent rather than a heavy border. That reasoning was sound and is
          // superseded, not refuted — the point now is that all three surfaces
          // (marketing, app, DoorlyPro) present the SAME closed state, and Eva's
          // face is the reveal when the panel opens rather than the thing on
          // screen the whole time.
          //
          // Same path as frontend/scripts/eva-widget.js so the two cannot drift.
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-4-.9L3 21l1.9-4.9A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/>
          </svg>
        )}
        {!open && unread > 0 && (
          <span style={{ position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%', background: '#ff082f', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread}</span>
        )}
      </button>}

      {/* ⚠️ maxHeight WAS A FLAT 520px ANCHORED FROM THE BOTTOM, so the panel's
          top edge landed 92 + 520 = 612px above the bottom inset — taller than
          the usable area on a shorter phone, and the header sat under the
          status bar where it could not be read or reached. Clamping to the
          space that actually exists (viewport, minus the launcher stack below,
          minus the top inset, minus a 12px breathing margin) means it shrinks
          on a small screen instead of overflowing off the top. The 520 stays as
          the cap wherever there is room for it. */}
      {open && (
        <div style={{ position: 'fixed', bottom: 'calc(104px + env(safe-area-inset-bottom, 0px))', right: 20, width: 360, maxWidth: 'calc(100vw - 40px)', maxHeight: 'min(520px, calc(100vh - 104px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px) - 12px))', background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', zIndex: 9998, overflow: 'hidden' }}>
          <div style={{ background: BLUE, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <EvaAvatar size={36} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>Eva</div>
              {/* 0.78 rather than 0.5 — the sublabel was set for the near-black
                  header; on Doorly blue that alpha drops below a legible
                  contrast against the background. */}
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)' }}>Doorly&rsquo;s AI Assistant</div>
            </div>
          </div>

          {/* minHeight removed with the clamp above: a 240px floor inside a
              panel that may now be shorter than 240px would push the composer
              back off the bottom, trading one cut-off edge for another. */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '82%', padding: '10px 13px', borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', background: m.role === 'user' ? BLUE : '#f3f4f6', color: m.role === 'user' ? '#fff' : DARK, fontSize: 13, lineHeight: '1.55' }}>
                  {m.content}
                </div>
                {/* Rateable only when the server gave us an id for this answer —
                    which excludes the client-seeded greeting and the local error
                    messages, neither of which is an Eva answer worth measuring. */}
                {m.role === 'assistant' && m.id && (
                  <EvaFeedback rating={m.rating ?? null} onRate={(v) => rateMessage(m.id, v)} />
                )}
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '10px 14px', background: '#f3f4f6', borderRadius: '16px 16px 16px 4px', display: 'flex', gap: 4, alignItems: 'center' }}>
                  {[0,1,2].map(i => (<div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', animation: `doorlyChatBounce 1.2s ease-in-out ${i * 0.2}s infinite` }}/>))}
                </div>
              </div>
            )}

            {/* ⚠️ EVA'S ONE QUESTION, ABOVE THE ESCALATION CONTROLS. It arrives
                once per conversation and disappears the moment it is answered —
                a prompt that lingers after being answered is the thing people
                learn to ignore, and the reason the thumbs beside every reply
                have never been used. */}
            {resolutionAsk && (
              <div style={{ background: '#edf1fb', border: `1px solid ${BLUE}33`, borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 8 }}>{resolutionAsk.question}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => answerResolution(1)}
                    style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', background: BLUE, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                    {resolutionAsk.yes}
                  </button>
                  <button onClick={() => answerResolution(-1)}
                    style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                    {resolutionAsk.no}
                  </button>
                </div>
              </div>
            )}
            {/* ⚠️ A "NO" IS NOT A DEAD END. Someone who has just told us we did
                not answer their question must be offered the way out in the same
                breath, or the honest answer costs them something. */}
            {resolutionDone === -1 && !escalated && (
              <div style={{ fontSize: 12.5, color: '#6b7280', textAlign: 'center' }}>
                Thanks — noted.{' '}
                <button onClick={() => setShowEscalate(true)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: BLUE, fontWeight: 700, fontSize: 12.5 }}>
                  Talk to a human
                </button>
              </div>
            )}
                    {/* ⚠️ THE SCALE REPLACES THE BUTTONS IN PLACE — the second half of one
                turn, not a second prompt. A fresh card asking "how did Eva do?"
                after they have just answered is two interruptions for one
                interaction, and the second is where people stop answering. */}
            {resolutionDone && csatScore === null && (
              <div style={{ background: '#edf1fb', border: `1px solid ${BLUE}33`, borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 8 }}>
                  {resolutionQuestion || 'How did Eva do?'}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => rateEva(n)}
                      style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid #d1d5db',
                        background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      {n}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Poor</span><span>Excellent</span>
                </div>
              </div>
            )}
            {csatScore !== null && (
              <div style={{ fontSize: 12.5, color: '#6b7280', textAlign: 'center' }}>Thanks — that helps.</div>
            )}
            {showEscalate && !escalated && (
              <div style={{ background: '#fff8ee', border: '1px solid #ffe4a0', borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 8 }}>How would you like us to reach you?</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {['phone', 'email', 'text'].map(opt => (
                    <button key={opt} onClick={() => setContactPref(opt)} style={{ flex: 1, padding: '6px 4px', borderRadius: 8, border: `1.5px solid ${contactPref === opt ? BLUE : '#e5e7eb'}`, background: contactPref === opt ? '#edf1fb' : '#fff', color: contactPref === opt ? BLUE : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>
                      {opt}
                    </button>
                  ))}
                </div>
                <input type={contactPref === 'email' ? 'email' : 'tel'} value={contactVal} onChange={e => setContactVal(e.target.value)} placeholder={contactPref === 'email' ? 'your@email.com' : '(512) 555-0100'}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}
                  onKeyDown={e => e.key === 'Enter' && handleEscalate()} />
                <button onClick={handleEscalate} disabled={!contactVal.trim()} style={{ width: '100%', padding: 9, background: contactVal.trim() ? DARK : '#e5e7eb', color: contactVal.trim() ? GREEN : '#9ca3af', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 800 }}>
                  Have a Team Member Follow Up
                </button>
              </div>
            )}

            {!showEscalate && !escalated && messages.length > 2 && (
              <div style={{ textAlign: 'center' }}>
                <button onClick={() => setShowEscalate(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#9ca3af', textDecoration: 'underline' }}>Talk to a human</button>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          <div style={{ padding: '12px 14px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, background: '#fff' }}>
            <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Ask a question..."
              style={{ flex: 1, padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', background: '#fff' }} />
            {/* ⚠️ WHITE, NOT THE BRAND GREEN. #acf44e on the dark button is about
                5.4:1 — it passes, and it reads as a disabled or secondary control
                next to the white text everywhere else in the composer. White on
                the same ground is ~16:1 and reads as the primary action it is. */}
            <button onClick={sendMessage} disabled={loading || !input.trim()}
              style={{ padding: '9px 14px', background: (loading || !input.trim()) ? '#e5e7eb' : DARK, color: (loading || !input.trim()) ? '#9ca3af' : '#fff', border: 'none', borderRadius: 10, cursor: (loading || !input.trim()) ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13 }}>
              Send
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes doorlyChatBounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }
        @keyframes doorlyPromptIn { from { opacity: 0; transform: translateX(6px); } to { opacity: 1; transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) { @keyframes doorlyPromptIn { from { opacity: 1; } to { opacity: 1; } } }
      `}</style>
    </>
  );
}
